import { devWarn, devError } from '@/utils/secureLogger';

interface PasswordValidationResult {
  valid: boolean;
  message?: string;
}

export const validatePassword = (password: string): PasswordValidationResult => {
  // Check for minimum length
  if (password.length < 8) {
    return {
      valid: false,
      message: "Password must be at least 8 characters long"
    };
  }
  
  // Check for complexity
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
  
  if (!(hasUpperCase && hasLowerCase && hasNumbers)) {
    return {
      valid: false,
      message: "Password must contain at least one uppercase letter, one lowercase letter, and one number"
    };
  }
  
  if (!hasSpecialChar) {
    return {
      valid: false,
      message: "Password must contain at least one special character"
    };
  }
  
  // Check for common passwords (simplified example - in production would use a larger list)
  const commonPasswords = [
    "password1", "qwerty123", "admin123", "welcome123", "123456789"
  ];
  
  if (commonPasswords.includes(password.toLowerCase())) {
    return {
      valid: false,
      message: "This password is too common. Please choose a more unique password."
    };
  }
  
  return { valid: true };
};

export const sanitizeInput = (input: string): string => {
  // Enhanced sanitization - remove HTML tags, scripts, and event handlers
  return input
    .replace(/(<([^>]+)>)/gi, "") // Remove HTML tags
    .replace(/javascript:/gi, "") // Remove javascript: URLs  
    .replace(/on\w+\s*=/gi, "") // Remove event handlers like onclick=
    .replace(/data:/gi, "") // Remove data URLs
    .replace(/vbscript:/gi, "") // Remove VBScript URLs
    .trim()
    .substring(0, 10000); // Limit length to prevent DoS
};

export const detectAnomalies = (userId: string, action: string): boolean => {
  // Enhanced anomaly detection with improved security
  const key = `user_actions_${userId.substring(0, 8)}`; // Only use partial user ID for privacy
  const now = Date.now();
  const timeWindow = 5 * 60 * 1000; // 5 minutes
  
  try {
    // Use sessionStorage for security logs (cleared when browser closes)
    const actionsData = sessionStorage.getItem(key);
    const actions = actionsData ? JSON.parse(actionsData) : [];
    
    // Clean up old actions
    const recentActions = actions.filter((a: any) => now - a.timestamp < timeWindow);
    
    // Add current action with limited data
    recentActions.push({
      action: sanitizeInput(action),
      timestamp: now,
      // Don't store full user agent for privacy
      browserType: navigator.userAgent.includes('Chrome') ? 'Chrome' : 'Other'
    });
    
    // Save updated actions to sessionStorage
    sessionStorage.setItem(key, JSON.stringify(recentActions));
    
    // Check for anomalies (simplified)
    // In a real system, you'd use more sophisticated methods
    if (recentActions.length > 20) { // Too many actions in short time window
      console.warn("Possible anomalous activity detected");
      return true;
    }
    
    // Check for rapid repeated actions
    const sameActions = recentActions.filter(a => a.action === action);
    if (sameActions.length > 10) {
      console.warn("Repeated action pattern detected");
      return true;
    }
  } catch (err) {
    console.error("Error tracking user actions:", err);
  }
  
  return false;
};
